import { useEffect } from "react";
import PublicLayout from "@/components/PublicLayout";

const ABOUT_IMAGE = "/images/about.png";
const poppins = { fontFamily: "'Poppins', sans-serif" };

const WHY_CARDS = [
  {
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#6C3084" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21 4 19 2c-2-2-4-2-5.5-.5L10 5 1.8 6.2c-.5.1-.9.6-.6 1.1l1.5 2.1c.2.3.6.5 1 .4L9 9l-4 4-2.5-.5c-.4-.1-.8.2-.9.6l-.5 2c-.1.5.3 1 .8 1l4 .5L9 20l.5 4c0 .5.5.9 1 .8l2-.5c.4-.1.7-.5.6-.9L12.5 21l4 4c1 1 2.5-.5 2-.8z"/>
      </svg>
    ),
    stat: "600+",
    label: "Happy Travellers",
    desc: "Hundreds of satisfied customers who found better fares and smoother journeys through our service.",
  },
  {
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#6C3084" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"/>
        <path d="M13 5v2M13 17v2M13 11v2"/>
      </svg>
    ),
    stat: "GDS",
    label: "Exclusive Fares",
    desc: "Access to professional booking systems and private fares not available on public booking sites.",
  },
  {
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#6C3084" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    ),
    stat: "Real",
    label: "People, Not Bots",
    desc: "Every request is handled by a real travel expert — personal, responsive, and always on your side.",
  },
  {
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#6C3084" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
        <polyline points="9 22 9 12 15 12 15 22"/>
      </svg>
    ),
    stat: "KvK",
    label: "Dutch Registered Agency",
    desc: "Registered travel services provider in the Netherlands (KvK 88159620) — professional and accountable.",
  },
];

const SERVICES = ["Flight Booking", "Visa Services", "Hotel Booking", "Travel Packages"];

export default function AboutPage() {
  useEffect(() => { document.title = "About Us | The Better Fare"; }, []);

  return (
    <PublicLayout>
      {/* Hero Banner */}
      <div style={{
        background: "linear-gradient(135deg, #6C3084, #4a1f6e)",
        padding: "60px 20px",
        textAlign: "center",
      }}>
        <h1 style={{ ...poppins, fontSize: "2.5rem", fontWeight: 700, color: "#fff", margin: "0 0 12px" }}>
          About The Better Fare
        </h1>
        <p style={{ ...poppins, fontSize: "1.1rem", color: "rgba(255,255,255,0.75)", margin: 0, textAlign: "center" }}>
          Online convenience. Personal service. Available on WhatsApp — wherever you are.
        </p>
      </div>

      {/* Our Story */}
      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p style={{ ...poppins, fontWeight: 700, fontSize: "0.75rem", color: "#7c3aed", textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: "12px" }}>
              Our Story
            </p>
            <h2 style={{ ...poppins, fontWeight: 700, fontSize: "1.75rem", color: "#1a1a1a", lineHeight: 1.3, marginBottom: "20px" }}>
              We found a gap. We filled it.
            </h2>
            <p style={{ ...poppins, fontSize: "1rem", color: "#555", lineHeight: 1.7, marginBottom: "16px" }}>
              Most travel agencies are either fully online with no personal touch, or traditional high-street offices with limited reach. We built The Better Fare to be different.
            </p>
            <p style={{ ...poppins, fontSize: "1rem", color: "#555", lineHeight: 1.7, marginBottom: "16px" }}>
              We are a Dutch-registered online travel agency — but with the personal service of a local agent. Have a question at 11pm before your trip? Message us on WhatsApp. Want to discuss complex multi-city routes? We're there.
            </p>
            <p style={{ ...poppins, fontSize: "1rem", color: "#555", lineHeight: 1.7, marginBottom: "16px" }}>
              The gap we noticed was simple: most travellers overpay for flights not because cheap fares don't exist, but because finding them takes time, expertise, and access to the right tools. We have all three — and we put them to work for you.
            </p>
            <p style={{ ...poppins, fontSize: "1rem", color: "#555", lineHeight: 1.7, marginBottom: "28px" }}>
              From Amsterdam to Islamabad, Vancouver to Karachi, or anywhere in between — we search across GDS systems, airlines, and aggregators to find fares that most booking sites simply don't surface.
            </p>
            <a
              href="/flight-request"
              style={{
                display: "inline-block",
                background: "#6C3084",
                color: "#fff",
                ...poppins,
                fontWeight: 600,
                fontSize: "1rem",
                padding: "14px 32px",
                borderRadius: "4px",
                textDecoration: "none",
              }}
            >
              Plan your next adventure
            </a>
          </div>
          <div className="hidden lg:flex justify-center">
            <img src={ABOUT_IMAGE} alt="The Better Fare team" className="w-full max-w-md" />
          </div>
        </div>
      </div>

      {/* Why Choose Us */}
      <div style={{ background: "#f3e8ff", borderTop: "1px solid #e9d5ff", borderBottom: "1px solid #e9d5ff" }}>
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="text-center mb-12">
            <p style={{ ...poppins, fontWeight: 700, fontSize: "0.75rem", color: "#7c3aed", textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: "8px" }}>
              Why Choose Us
            </p>
            <h2 style={{ ...poppins, fontWeight: 700, fontSize: "1.75rem", color: "#1a1a1a" }}>
              Built on trust, driven by results
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {WHY_CARDS.map(({ icon, stat, label, desc }) => (
              <div key={label} style={{ background: "#fff", borderRadius: "12px", padding: "24px", border: "1px solid #e9d5ff", textAlign: "center" }}>
                <div style={{ display: "flex", justifyContent: "center", marginBottom: "12px" }}>{icon}</div>
                <div style={{ ...poppins, fontWeight: 900, fontSize: "1.5rem", color: "#6C3084", marginBottom: "4px" }}>{stat}</div>
                <div style={{ ...poppins, fontWeight: 700, fontSize: "0.9rem", color: "#1a1a1a", marginBottom: "10px" }}>{label}</div>
                <p style={{ ...poppins, fontSize: "0.825rem", color: "#666", lineHeight: 1.6, margin: 0 }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Our Services */}
      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-center mb-8">
          <p style={{ ...poppins, fontWeight: 700, fontSize: "0.75rem", color: "#7c3aed", textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: "8px" }}>
            What We Do
          </p>
          <h2 style={{ ...poppins, fontWeight: 700, fontSize: "1.75rem", color: "#1a1a1a", marginBottom: "16px" }}>
            Our Services
          </h2>
          <p style={{ ...poppins, fontSize: "1rem", color: "#555", marginBottom: "28px" }}>
            From booking your first flight to handling your visa application — we take care of it all.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {SERVICES.map((s) => (
              <span key={s} style={{
                background: "#f3e8ff",
                border: "1px solid #e9d5ff",
                color: "#4a1d72",
                ...poppins,
                fontSize: "0.875rem",
                fontWeight: 600,
                padding: "8px 20px",
                borderRadius: "999px",
              }}>
                {s}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Mission & Values */}
      <div style={{ background: "#f9f7fc", borderTop: "1px solid #f0f0f0", borderBottom: "1px solid #f0f0f0" }}>
        <div className="max-w-5xl mx-auto px-6 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="bg-white rounded-xl p-8 border border-gray-100">
              <h3 style={{ ...poppins, fontWeight: 700, fontSize: "1.25rem", color: "#6C3084", marginBottom: "12px" }}>Our Mission</h3>
              <p style={{ ...poppins, fontSize: "0.95rem", color: "#555", lineHeight: 1.7 }}>
                To make travel accessible and affordable for everyone. We believe that finding the perfect flight shouldn't be stressful or expensive. Our team works tirelessly to negotiate the best rates and provide exceptional customer service.
              </p>
            </div>
            <div className="bg-white rounded-xl p-8 border border-gray-100">
              <h3 style={{ ...poppins, fontWeight: 700, fontSize: "1.25rem", color: "#6C3084", marginBottom: "12px" }}>Our Values</h3>
              <p style={{ ...poppins, fontSize: "0.95rem", color: "#555", lineHeight: 1.7 }}>
                Transparency, reliability, and customer satisfaction are at the heart of everything we do. We pride ourselves on honest pricing, clear communication, and going the extra mile for our travelers.
              </p>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
